import {useCallback, useEffect, useMemo} from 'react';
import {useQueryParams} from 'presentation/pages';
import {useGetExamsDatesQuery} from '../../queries/useGetExamsDatesQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useGetExamsMultiAvailableTimesQuery} from '../../queries/useGetExamsMultiAvailableTimesQuery';
import {useGetExamsAvailableTimesQuery} from '../../queries/useGetExamsAvailableTimesQuery';

import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {
	ExamsAvailableTimes,
	ExamsMultiAvailableTimes,
	HandleHourSelectionParams,
	UseExamProfessionalSelectionProps,
} from './types';
import {useExamBooking} from 'presentation/modules/booking/exam/hooks/useExamBooking';
import {useExamSearchBar} from 'presentation/modules/booking/exam/ui/components/ExamSearchBar/hooks/useExamSearchBar';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {textHandle} from 'presentation/utils/textHandle';
import {useIsAdvanceDiagnosticsExamsJourney} from 'presentation/modules/booking/exam/hooks/useIsAdvanceDiagnosticsExamsJourney';
import {translations} from 'presentation/translations-new';

const {examRoom} = translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function useExamProfessionalSelection({handleNextStep}: UseExamProfessionalSelectionProps) {
	const {SEARCH_PARAMS} = useQueryParams();
	const {activeBookingFlowStep} = useExamBooking();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const {handleClearShiftAndSearch} = useExamSearchBar({activeStep: activeBookingFlowStep});
	const isAdvanceDiagnosticsExamsJourneyEnabled = useIsAdvanceDiagnosticsExamsJourney();

	const {shift, calendarActive, query} = SEARCH_PARAMS;

	const isCalendarActive = !!(calendarActive && calendarActive === 'true');
	const shouldUseUniqueDate = Boolean(isCalendarActive && bookingData.dateAt);
	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const showSelectedDateProfessionalGrid = shouldUseUniqueDate;
	const showClearFilterButton = Boolean(shift || isCalendarActive || query);

	const partialAvailableTimesParams = useMemo(
		() => ({
			cdTipoExame: String(bookingData.specialtyCode),
			cdPrestadorJuridico: String(bookingData.unitCode),
			filtroTurno: shift,
		}),
		[bookingData.specialtyCode, bookingData.unitCode, shift],
	);

	const {
		data: availableDates,
		isFetching: isFetchingAvailableDates,
		error: errorAvailableDates,
		refetch: availableDatesRefetch,
	} = useGetExamsDatesQuery({enabled: true});

	const {
		data: examsMultiAvailableTimes,
		isFetching: isFetchingExamsMultiAvailableTimes,
		error: errorExamsMultiAvailableTimes,
		refetch: examsMultiAvailableTimesRefetch,
	} = useGetExamsMultiAvailableTimesQuery({
		params: partialAvailableTimesParams,
		dates: availableDates,
		enabled: !!availableDates && !shouldUseUniqueDate,
	});

	const {
		data: examsAvailableTimes,
		isFetching: isFetchingExamsAvailableTimes,
		error: errorExamsAvailableTimes,
		refetch: examsAvailableTimesRefetch,
	} = useGetExamsAvailableTimesQuery({
		params: {
			...partialAvailableTimesParams,
			dtData: bookingData.dateAt,
		},
		enabled: shouldUseUniqueDate,
	});

	const withExamRoomName = useCallback(
		(times: ExamsAvailableTimes[] = []) => {
			if (!isAdvanceDiagnosticsExamsJourneyEnabled) return times;

			return times.map((time, index) => ({
				...time,
				professionalName: `${examRoom} ${index + 1}`,
			}));
		},
		[isAdvanceDiagnosticsExamsJourneyEnabled],
	);

	const examsAvailableTimesFiltered = useMemo(() => {
		if (!examsAvailableTimes) return examsAvailableTimes;

		const times = withExamRoomName(examsAvailableTimes);

		if (!queryToFilter) return times;

		return times.filter(({professionalName}) =>
			textHandle(professionalName).sanitizeText().includes(queryToFilter),
		);
	}, [examsAvailableTimes, queryToFilter, withExamRoomName]) as ExamsAvailableTimes[];

	const examsMultiAvailableTimesFiltered = useMemo(() => {
		if (!examsMultiAvailableTimes) return examsMultiAvailableTimes;

		const multiTimes = examsMultiAvailableTimes.map(availableTime => ({
			...availableTime,
			times: withExamRoomName(availableTime.times),
		}));

		if (!queryToFilter) return multiTimes;

		return multiTimes
			.filter(({times}) => times.length > 0)
			.map(availableTime => ({
				...availableTime,
				times: availableTime.times.filter(({professionalName}) =>
					textHandle(professionalName).sanitizeText().includes(queryToFilter),
				),
			}));
	}, [examsMultiAvailableTimes, queryToFilter, withExamRoomName]) as ExamsMultiAvailableTimes[];

	const examsMultiAvailableTimesHasNoAvailableTimes = examsMultiAvailableTimesFiltered?.every(
		list => list.times.length === 0,
	);

	const examsAvailableTimesHasNoAvailableTimes = examsAvailableTimesFiltered?.length === 0;

	const notFoundAvailableDatesError = isUnprocessableError(errorAvailableDates);
	const notFoundExamsAvailableTimesError = isUnprocessableError(errorExamsAvailableTimes);
	const notFoundExamsMultiAvailableTimesError = isUnprocessableError(
		errorExamsMultiAvailableTimes,
	);

	const notFoundProfessionalsError =
		notFoundAvailableDatesError ||
		notFoundExamsMultiAvailableTimesError ||
		notFoundExamsAvailableTimesError;

	const isLoading =
		isFetchingAvailableDates ||
		isFetchingExamsMultiAvailableTimes ||
		isFetchingExamsAvailableTimes;

	const isError =
		!notFoundProfessionalsError &&
		(errorAvailableDates || errorExamsMultiAvailableTimes || errorExamsAvailableTimes);

	const hasNoAvailableDates =
		!isFetchingAvailableDates && (!availableDates || availableDates.length === 0);

	const showNotFoundState =
		hasNoAvailableDates ||
		examsMultiAvailableTimesHasNoAvailableTimes ||
		examsAvailableTimesHasNoAvailableTimes ||
		notFoundProfessionalsError;

	const shouldHideSearchBar = !!errorAvailableDates || hasNoAvailableDates;

	const notFoundStateDataTagId = useCallback(() => {
		if (hasNoAvailableDates) {
			return FinishWithoutCompleteFlowEnum.SEM_HORARIOS_AGENDAMENTO_ENCONTRADO;
		}

		return showClearFilterButton
			? FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_ENCONTRADO
			: FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_DISPONIVEL;
	}, [hasNoAvailableDates, showClearFilterButton]);

	const handleSelectHour = useCallback(
		({profCode, profName, date, hour}: HandleHourSelectionParams) => {
			setBookingData(prev => ({
				...prev,
				professionalCode: profCode,
				professionalName: profName,
				dateAt: date,
				hourAt: hour,
			}));

			handleNextStep();
		},
		[setBookingData, handleNextStep],
	);

	const refetch = useCallback(() => {
		if (errorAvailableDates) {
			return availableDatesRefetch();
		}

		return shouldUseUniqueDate
			? examsAvailableTimesRefetch()
			: examsMultiAvailableTimesRefetch();
	}, [
		errorAvailableDates,
		shouldUseUniqueDate,
		availableDatesRefetch,
		examsAvailableTimesRefetch,
		examsMultiAvailableTimesRefetch,
	]);

	useEffect(() => {
		setHideSearchBarState(shouldHideSearchBar);
	}, [shouldHideSearchBar, setHideSearchBarState]);

	return {
		examsAvailableTimesFiltered,
		examsMultiAvailableTimesFiltered,
		isLoading,
		isError,
		showNotFoundState,
		refetch,
		showSelectedDateProfessionalGrid,
		showClearFilterButton,
		selectedDate: bookingData.dateAt,
		handleSelectHour,
		handleClearShiftAndSearch,
		notFoundStateDataTagId,
		query,
	};
}
